import React, { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || "en");

  // Initialize language from localStorage or default to 'en'
  useEffect(() => {
    const supportedLanguages = ["en", "ar", "de", "ja", "tr"];
    const savedLanguage = localStorage.getItem("language");
    if (savedLanguage && supportedLanguages.includes(savedLanguage)) {
      changeLanguage(savedLanguage);
    } else {
      // Detect browser language
      const browserLang = navigator.language?.toLowerCase().slice(0, 2);
      if (supportedLanguages.includes(browserLang)) {
        changeLanguage(browserLang);
      } else {
        changeLanguage("en");
      }
    }
  }, []);

  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    setCurrentLanguage(language);
    localStorage.setItem("language", language);

    // Set document direction for RTL languages
    if (language === "ar") {
      document.documentElement.dir = "rtl";
      document.documentElement.lang = "ar";
    } else {
      document.documentElement.dir = "ltr";
      document.documentElement.lang = language;
    }
  };

  const toggleLanguage = () => {
    // Cycle through en, ar, de, ja, tr
    const supportedLanguages = ["en", "ar", "de", "ja", "tr"];
    const currentIndex = supportedLanguages.indexOf(currentLanguage);
    const nextIndex = (currentIndex + 1) % supportedLanguages.length;
    const newLanguage = supportedLanguages[nextIndex];
    changeLanguage(newLanguage);
  };

  // Arabic is currently the only RTL language in the app
  const isRTL = currentLanguage === "ar";

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        changeLanguage,
        toggleLanguage,
        isRTL,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
